import { createContext, useContext, useState, useCallback, useRef } from 'react';
import { X, CheckCircle, AlertCircle, Info } from 'lucide-react';

const ToastContext = createContext(null);

const ICONS = {
  success: <CheckCircle size={16} className="text-green-500 shrink-0 mt-0.5" />,
  error:   <AlertCircle  size={16} className="text-red-500 shrink-0 mt-0.5" />,
  info:    <Info         size={16} className="text-[#00DBFF] shrink-0 mt-0.5" />,
};

const BG = {
  success: 'border-green-200 bg-white',
  error:   'border-red-200 bg-white',
  info:    'border-[#00DBFF]/30 bg-white',
};

function Toast({ id, type, message, action, onDismiss }) {
  return (
    <div
      role="alert"
      aria-live="assertive"
      className={`flex items-start gap-3 px-4 py-3 rounded-lg border shadow-lg text-sm max-w-sm pointer-events-auto ${BG[type]}`}
    >
      {ICONS[type]}
      <div className="flex-1 min-w-0">
        <p className="text-gray-700 leading-snug">{message}</p>
        {action && (
          <button
            type="button"
            onClick={() => { action.onClick(); onDismiss(id); }}
            className="mt-1.5 text-xs font-semibold text-[#00A8C7] hover:text-[#002233] hover:underline transition-colors"
          >
            {action.label}
          </button>
        )}
      </div>
      <button
        onClick={() => onDismiss(id)}
        className="shrink-0 text-gray-400 hover:text-gray-600 transition-colors"
        aria-label="Fechar"
      >
        <X size={14} />
      </button>
    </div>
  );
}

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const timerRef = useRef({});
  let nextId = useRef(0);

  const dismiss = useCallback((id) => {
    clearTimeout(timerRef.current[id]);
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  const show = useCallback((type, message, duration = 4000, action = null) => {
    const id = ++nextId.current;
    setToasts(prev => [...prev.slice(-4), { id, type, message, action }]);
    if (duration > 0) {
      timerRef.current[id] = setTimeout(() => dismiss(id), duration);
    }
    return id;
  }, [dismiss]);

  const toast = {
    success: (msg, dur, action) => show('success', msg, dur, action),
    error:   (msg, dur, action) => show('error',   msg, dur, action),
    info:    (msg, dur, action) => show('info',    msg, dur, action),
  };

  return (
    <ToastContext.Provider value={toast}>
      {children}
      <div
        aria-live="polite"
        className="fixed bottom-6 right-4 z-[300] flex flex-col gap-2 pointer-events-none"
      >
        {toasts.map(t => (
          <Toast key={t.id} {...t} onDismiss={dismiss} />
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used inside ToastProvider');
  return ctx;
}
